import { defineComponent } from 'vue'
import { TransformerInterface } from '~/store/analyzer/list'
import { RepoConfigInterface } from '~/store/repository/detail'
import { AnalyzerAnalyzerType } from '~/types/types'

const TAB = '  '
const NEWLINE = '\n'
const INDENT = `${NEWLINE}${TAB}`

export const CONFIG_MAP_ANALYZER_TYPE = {
  [AnalyzerAnalyzerType.Core]: AnalyzerAnalyzerType.Core,
  [AnalyzerAnalyzerType.Community]: AnalyzerAnalyzerType.Community,
  [AnalyzerAnalyzerType.Custom]: AnalyzerAnalyzerType.Custom,
  core: AnalyzerAnalyzerType.Core,
  community: AnalyzerAnalyzerType.Community,
  custom: AnalyzerAnalyzerType.Custom
}

/**
 * Parses an array of strings and returns a formatted string representation.
 *
 * @param arr - The array of strings to parse.
 * @param indent - The number of indentation levels for the formatted string. Default is 1.
 * @returns A formatted string representation of the parsed array.
 */
function parseArray(arr: string[], indent = 1) {
  if (arr.length === 0) {
    return ''
  }
  if (arr.length === 1) {
    return JSON.stringify(arr)
  }

  const candidates = arr
    .filter(Boolean)
    .map((val: string): string => {
      return JSON.stringify(val)
    })
    .join(`,${NEWLINE}${TAB.repeat(indent)}`)
  return `[${NEWLINE}${TAB.repeat(indent)}${candidates}${NEWLINE}${TAB.repeat(indent - 1)}]`
}

export default defineComponent({
  name: 'TomlGeneratorMixin',

  data() {
    return {
      CONFIG_MAP_ANALYZER_TYPE
    }
  },

  methods: {
    tomlTemplate(
      config: RepoConfigInterface,
      testPatterns: RepoConfigInterface['test_patterns'],
      excludePatterns: RepoConfigInterface['exclude_patterns']
    ): string {
      const toml = [`version = ${JSON.stringify(config.version)}`]

      if (testPatterns?.length) {
        if (testPatterns.filter(Boolean).length) {
          toml.push(this.testPatternsTemplate(testPatterns))
        }
      }

      if (excludePatterns?.length) {
        if (excludePatterns.filter(Boolean).length) {
          toml.push(this.excludePatternsTemplate(excludePatterns))
        }
      }

      if (config?.analyzers?.length) {
        toml.push(this.analyzersTemplate(config.analyzers))
      }

      if (config?.transformers?.length || config?.code_formatters?.length) {
        toml.push(
          this.transformersTemplate(
            config.code_formatters ?? config.transformers ?? [],
            config.transformers?.length ? 'transformers' : 'code_formatters'
          )
        )
      }

      return toml.join(NEWLINE.repeat(2))
    },

    testPatternsTemplate(testPatterns: string[]): string {
      return testPatterns.length ? `test_patterns = ${parseArray(testPatterns)}` : ''
    },

    excludePatternsTemplate(excludePatterns: string[]): string {
      return excludePatterns.length ? `exclude_patterns = ${parseArray(excludePatterns)}` : ''
    },

    analyzersTemplate(allAnalyzers: RepoConfigInterface['analyzers']): string {
      return allAnalyzers
        .map((analyzer) => {
          const headerLines = ['[[analyzers]]', `name = ${JSON.stringify(analyzer.name)}`]
          if (this.CONFIG_MAP_ANALYZER_TYPE[analyzer.type] === AnalyzerAnalyzerType.Community) {
            headerLines.push('type = "community"')
          }
          if (!analyzer.enabled) {
            headerLines.push('enabled = false')
          }

          const header = headerLines.join(NEWLINE)

          const meta = [header]

          if (analyzer.meta) {
            const metaOptions = Object.keys(analyzer.meta)
              .map((metaAttribute: string) => {
                if (!analyzer.meta) return ''
                const val = analyzer.meta[metaAttribute] as string[] | string | number | boolean
                if (Array.isArray(val)) {
                  if (val.length) {
                    return `${metaAttribute} = ${parseArray(val, 2)}`
                  }
                  return null
                }
                if (val || val === false || val === 0) {
                  return `${metaAttribute} = ${JSON.stringify(val)}`
                }
                return null
              })
              .filter((opt) => opt) // Remove all empty values

            if (metaOptions.length) {
              meta.push(
                [
                  '', // skip a line
                  '[analyzers.meta]',
                  ...metaOptions
                ].join(INDENT)
              )
            }
          }

          return meta.join(NEWLINE)
        })
        .join(NEWLINE.repeat(2))
    },

    transformersTemplate(
      allTransformers: NonNullable<
        RepoConfigInterface['transformers'] | RepoConfigInterface['code_formatters']
      >,
      key: 'transformers' | 'code_formatters' = 'transformers'
    ): string {
      return allTransformers
        .map((transformer: TransformerInterface) => {
          const headerLines = [`[[${key}]]`, `name = ${JSON.stringify(transformer.name)}`]
          if (!transformer.enabled) headerLines.push('enabled = false')

          return headerLines.join(NEWLINE)
        })
        .join(NEWLINE.repeat(2))
    }
  }
})
